export const SYNERGY_TESTNET_CHAIN_ID = 1266;
export const SYNERGY_TESTNET_CHAIN_HEX = "0x4f2";
export const SYNERGY_LEGACY_TESTNET_CHAIN_ID = 1264;
export const SYNERGY_LEGACY_TESTNET_CHAIN_HEX = "0x4f0";

export type SynergyProvider = {
  request?: (request: { method: string; params?: unknown[] }) => Promise<unknown>;
  requestAccounts?: () => Promise<unknown>;
  getChainId?: () => Promise<unknown>;
  synergy_signMessage?: (message: unknown, account?: string) => Promise<unknown>;
  on?: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener?: (event: string, handler: (...args: unknown[]) => void) => void;
};

export type ConnectedWallet = {
  address: string;
  chainId: number;
  provider?: SynergyProvider;
  source?: "browser" | "wallet-link";
  walletLinkSession?: unknown;
};

export type SynergyWalletMessageProof = {
  signatureAlgorithm: 'FN-DSA-1024';
  signatureHex: string;
  publicKeyHex: string;
  account: string;
  origin: string;
  method: 'synergy_signMessage';
  payload: unknown;
};

type WalletWindow = Window & {
  synergy?: SynergyProvider;
  SynergyWallet?: SynergyProvider;
  synergyWallet?: SynergyProvider;
};

function request(provider: SynergyProvider, method: string, params: unknown[] = []) {
  if (typeof provider.request !== "function") {
    throw new Error("Synergy Wallet does not expose a compatible browser provider.");
  }
  return provider.request({ method, params });
}

function extractAddress(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return extractAddress(value[0]);
  if (value && typeof value === "object") {
    const account = value as Record<string, unknown>;
    return extractAddress(account.synergyAddress ?? account.account ?? account.address ?? account.walletAddress);
  }
  return "";
}

function signatureProof(value: unknown, walletAddress: string): SynergyWalletMessageProof {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Synergy Wallet did not return a message signature.");
  }
  const proof = value as Record<string, unknown>;
  if (proof.signatureAlgorithm !== "FN-DSA-1024" || typeof proof.signatureHex !== "string" || typeof proof.publicKeyHex !== "string"
    || typeof proof.account !== "string" || typeof proof.origin !== "string" || proof.method !== "synergy_signMessage" || !("payload" in proof)) {
    throw new Error("Synergy Wallet returned an unsupported message-signature format.");
  }
  if (proof.account.trim().toLowerCase() !== walletAddress.trim().toLowerCase()) {
    throw new Error("Synergy Wallet signed with an account other than the connected wallet.");
  }
  return proof as SynergyWalletMessageProof;
}

export function normalizeChainId(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "bigint") return Number(value);
  if (typeof value !== "string") return null;
  const text = value.trim().toLowerCase();
  if (!text) return null;
  const parsed = text.startsWith("0x") ? Number.parseInt(text, 16) : Number.parseInt(text, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getInjectedSynergyProvider(windowLike: WalletWindow | undefined = window): SynergyProvider | null {
  return windowLike?.synergy ?? windowLike?.SynergyWallet ?? windowLike?.synergyWallet ?? null;
}

export async function connectSynergyWallet(provider = getInjectedSynergyProvider()): Promise<ConnectedWallet> {
  if (!provider) {
    throw new Error("Synergy Wallet was not found. Install or unlock the browser extension, then try again.");
  }

  let accounts: unknown;
  try {
    accounts = typeof provider.requestAccounts === "function" ? await provider.requestAccounts() : await request(provider, "synergy_requestAccounts");
  } catch {
    accounts = await request(provider, "synergy_accounts");
  }
  const address = extractAddress(accounts);
  if (!address) throw new Error("Synergy Wallet did not return an account address.");

  let rawChainId: unknown;
  try {
    rawChainId = typeof provider.getChainId === "function" ? await provider.getChainId() : await request(provider, "synergy_chainId");
  } catch {
    rawChainId = await request(provider, "synergy_getChainId");
  }
  const chainId = normalizeChainId(rawChainId);
  if (chainId !== SYNERGY_TESTNET_CHAIN_ID && chainId !== SYNERGY_LEGACY_TESTNET_CHAIN_ID) {
    throw new Error(`Synergy Wallet is on chain ${String(rawChainId || "unknown")}; Horizon requires Synergy Testnet (${SYNERGY_TESTNET_CHAIN_HEX} or ${SYNERGY_LEGACY_TESTNET_CHAIN_HEX}).`);
  }
  return { address, chainId, provider };
}

export async function signSynergyWalletMessage(wallet: ConnectedWallet, payload: unknown): Promise<SynergyWalletMessageProof> {
  const { provider, address } = wallet;
  if (!provider) throw new Error("This Synergy Wallet session signs messages through Wallet Link.");
  const signed = typeof provider.synergy_signMessage === "function"
    ? await provider.synergy_signMessage(payload, address)
    : await request(provider, "synergy_signMessage", [payload, address]);
  return signatureProof(signed, address);
}

export function shortenAddress(address: string) {
  return address.length > 13 ? `${address.slice(0, 7)}…${address.slice(-5)}` : address;
}
